import { useEffect, useRef, useState } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';

import Dialog from 'src/componentes/dialog';
import Table from 'src/componentes/table';
import LoadingContainer from 'src/componentes/loading-container';
import Button from 'src/componentes/button';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import formatDateFns from 'src/utils/formatDateFns';
import { capitalizeFirstLetter } from 'src/utils/string';
import 'src/paginas/gestao-risco/fluxo-aprovacao/style.css';
import StatusBadge from 'src/componentes/StatusBadge';

const HISTORICO_FLUXO_APROVACAO_STATUS_RISCO_URL =
  '/SolicitacaoGestaoMudancasValidacaoAprovacao/ObterHistoricoFluxoAprovacaoStatusRisco';

const ModalHistoricoFluxoAprovacaoStatus = ({
  open = true,
  onClose,
  riscoId,
  fluxoAprovacaoNome,
  title,
  ...rest
}) => {
  const fluxoStatusStyle = {
    fontFamily: 'Nunito',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '100%',
    letterSpacing: 0,
    verticalAlign: 'middle',
  };
  const cabecalhoInfoStyle = {
    fontFamily: 'Nunito',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '100%',
    letterSpacing: 0,
    verticalAlign: 'middle',
  };

  const ref = useRef();
  const intl = useIntl();
  const { user, terms: resources } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(
    user?.idioma,
    true,
    true
  );
  const [isLoading, setIsLoading] = useState(false);
  const [historicoData, setHistoricoData] = useState([]);

  useEffect(() => {
    if (!open) return;

    const hasValidRiscoId =
      riscoId !== null &&
      riscoId !== undefined &&
      riscoId !== '' &&
      Number(riscoId) > 0;

    if (!hasValidRiscoId) {
      setIsLoading(false);
      setHistoricoData([]);
      return;
    }

    const handleGetHistoricoFluxoAprovacao = async () => {
      setIsLoading(true);
      try {
        const response = await http.post(
          HISTORICO_FLUXO_APROVACAO_STATUS_RISCO_URL,
          {
            riscoId,
          }
        );

        setHistoricoData(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        const errorMessage = String(
          error?.response?.data?.errorMessage || error?.message || ''
        ).toLowerCase();
        const isNotFoundError =
          error?.response?.status === 404 || errorMessage === 'notfound';

        if (isNotFoundError) {
          setHistoricoData([]);
          return;
        }

        errorHandler(error);
      } finally {
        setIsLoading(false);
      }
    };

    handleGetHistoricoFluxoAprovacao();
  }, [open, riscoId]);

  if (!open) return null;

  const handleClose = () => {
    if (onClose) onClose();
    if (ref.current) ref.current.close();
  };

  const formatDateValue = (value) => {
    if (!value) return '-';

    try {
      return format(new Date(value), currentDisplay, { locale: currentLocale });
    } catch (error) {
      return '-';
    }
  };

  const formatBoolean = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value !== 'boolean') return value;
    return value
      ? intl.formatMessage({ id: 'sim' })
      : intl.formatMessage({ id: 'nao' });
  };

  const getStatus = (item) => item?.status?.descricao || item?.status || '-';

  const formatTipoSolicitacao = (tipoSolicitacao) => {
    if (!tipoSolicitacao) return '-';

    return intl.formatMessage(
      { id: tipoSolicitacao, defaultMessage: tipoSolicitacao },
      { risco: resources.risco }
    );
  };

  const tituloModal =
    title ||
    `${intl.formatMessage({ id: 'label.historico' })}${
      fluxoAprovacaoNome ? ` - ${fluxoAprovacaoNome}` : ''
    }`;

  const modalActions = [
    <Button key="btn-fechar-historico" color="secondary" onClick={handleClose}>
      {intl.formatMessage({ id: 'label.fechar' })}
    </Button>,
  ];

  const renderResponsaveis = (
    responsaveis,
    nivelHierarquico,
    responsabilidade,
    rowKey
  ) => {
    const tooltipId = `tooltip-nivel-hierarquico-${rowKey}`;

    const hasNivelHierarquico =
      nivelHierarquico !== null &&
      nivelHierarquico !== undefined &&
      nivelHierarquico !== '';

    const hasResponsabilidade =
      responsabilidade !== null &&
      responsabilidade !== undefined &&
      responsabilidade !== '';

    const tooltipText = hasNivelHierarquico
      ? nivelHierarquico
      : hasResponsabilidade
      ? responsabilidade
      : null;

    if (Array.isArray(responsaveis) && responsaveis.length === 0) {
      return (
        <div>
          <span
            id={tooltipId}
            style={{ color: '#6C757D', fontStyle: 'italic' }}
          >
            {intl.formatMessage(
              { id: 'labelNenhumResponsavelEncontrado' },
              { responsavel: resources?.responsavel }
            )}
          </span>

          {tooltipText && (
            <UncontrolledTooltip placement="bottom" target={tooltipId}>
              {tooltipText}
            </UncontrolledTooltip>
          )}
        </div>
      );
    }

    return responsaveis;
  };

  const renderStatus = (status) => {
    if (!status) return '-';

    return (
      <StatusBadge
        text={status?.descricao || '-'}
        textColor={status?.cor}
        backgroundColor={status?.corFundo}
      />
    );
  };

  return (
    <Dialog
      ref={ref}
      title={tituloModal}
      width="80%"
      height="80%"
      padContent
      actions={modalActions}
      onRequestClose={handleClose}
      {...rest}
    >
      <LoadingContainer isLoading={isLoading}>
        {historicoData && historicoData.length > 0 ? (
          <div className="historico-content">
            {historicoData.map((solicitacao, index) => {
              const cabecalho = solicitacao?.cabecalho;
              const historico = solicitacao?.historico || [];
              const solKey = solicitacao?.solicitacaoId
                ? `sol-${solicitacao.solicitacaoId}-${index}`
                : `sol-${index}`;

              return (
                <div key={solKey} className="historico-item">
                  <div className="mb-3">
                    <div className="mb-2" style={fluxoStatusStyle}>
                      {`Fluxo ${cabecalho?.ultimoStatus || '-'}`}
                    </div>
                    <div className="d-flex flex-wrap">
                      <div className="mr-4 mb-2" style={cabecalhoInfoStyle}>
                        <span>
                          {intl.formatMessage({ id: 'labelTipoSolicitacao' })}:
                        </span>{' '}
                        <span>
                          {formatTipoSolicitacao(cabecalho?.tipoSolicitacao)}
                        </span>
                      </div>
                      <div className="mr-4 mb-2" style={cabecalhoInfoStyle}>
                        <span>
                          {intl.formatMessage({ id: 'fluxoAprovacao' })}:
                        </span>{' '}
                        <span>{cabecalho?.fluxoAprovacao?.nome}</span>
                      </div>
                      <div className="mr-4 mb-2" style={cabecalhoInfoStyle}>
                        <span>{intl.formatMessage({ id: 'dataEnvio' })}:</span>{' '}
                        <span>{formatDateValue(cabecalho?.dataEnvio)}</span>
                      </div>
                      <div className="mb-2" style={cabecalhoInfoStyle}>
                        <span>
                          {intl.formatMessage({ id: 'labelEnviadoPor' })}:
                        </span>{' '}
                        <span>{cabecalho?.enviadoPor || '-'}</span>
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <div className="d-flex gap-1">
                        <div>
                          {intl.formatMessage({ id: 'labelStatusAnterior' })}:
                        </div>
                        <div>{renderStatus(cabecalho?.statusAnterior)}</div>
                      </div>
                      <div className="d-flex gap-1">
                        <div>
                          {intl.formatMessage({ id: 'labelStatusSolicitado' })}:
                        </div>
                        <div>{renderStatus(cabecalho?.statusSolicitado)}</div>
                      </div>
                    </div>
                  </div>

                  <Table
                    responsive
                    listagemContainer={false}
                    styleDiv={{ maxHeight: 540 }}
                    className="mb-0"
                  >
                    <thead>
                      <tr>
                        <th>{capitalizeFirstLetter(resources.responsaveis)}</th>
                        <th>{intl.formatMessage({ id: 'nomeEtapa' })}</th>
                        <th>{intl.formatMessage({ id: 'etapa' })}</th>
                        <th>{intl.formatMessage({ id: 'status' })}</th>
                        <th>
                          {intl.formatMessage({ id: 'labelDataAprovacao' })}
                        </th>
                        <th>{intl.formatMessage({ id: 'labelTempoEtapa' })}</th>
                        <th>{intl.formatMessage({ id: 'aprovacaoUnica' })}</th>
                        <th>
                          {intl.formatMessage({ id: 'label.comentarios' })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {historico.length > 0 ? (
                        historico.map((item, itemIndex) => {
                          const rowKey =
                            item?.id || `${solKey}-row-${itemIndex}`;

                          return (
                            <tr key={rowKey}>
                              <td>
                                {renderResponsaveis(
                                  item?.responsaveis,
                                  item?.nivelHierarquico,
                                  item?.responsabilidade,
                                  rowKey
                                )}
                              </td>
                              <td>{item?.nomeEtapa || '-'}</td>
                              <td>{item?.etapa || '-'}</td>
                              <td>{getStatus(item)}</td>
                              <td>{formatDateValue(item?.dataAprovacao)}</td>
                              <td>{item?.duracao ?? '-'}</td>
                              <td>{formatBoolean(item?.aprovacaoUnica)}</td>
                              <td
                                style={{
                                  wordBreak: 'break-word',
                                  maxWidth: 300,
                                }}
                              >
                                {item?.observacoes || '-'}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center py-4">
                            {intl.formatMessage({
                              id: 'label.nenhumRegistroEncontrado',
                            })}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-4">
            {intl.formatMessage({ id: 'label.nenhumRegistroEncontrado' })}
          </div>
        )}
      </LoadingContainer>
    </Dialog>
  );
};

export default ModalHistoricoFluxoAprovacaoStatus;
